import { cn } from "@/lib/utils";
import React, { forwardRef } from "react";

export const Row = forwardRef<HTMLDivElement, FlexBoxProps>(
  ({ children, ...rest }, ref) => (
    <FlexBox direction="row" ref={ref} {...rest}>
      {children}
    </FlexBox>
  ),
);

Row.displayName = "Row";

export const Column = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithRef<typeof FlexBox>
>(({ children, ...rest }: React.ComponentPropsWithRef<typeof FlexBox>, ref) => {
  return (
    <FlexBox direction="column" ref={ref} {...rest}>
      {children}
    </FlexBox>
  );
});

Column.displayName = "Column";

interface FlexBoxBaseProps
  extends React.PropsWithChildren,
    React.HTMLAttributes<HTMLDivElement> {
  justify?: "center" | "start" | "end" | "space-between";
  align?: "center" | "start" | "end";
  alignSelf?: "center" | "start" | "end" | "stretch";
  flex?: true | string;
  className?: string;
  scrollableParent?: boolean;
  scrollable?: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}

interface FlexBoxProps extends FlexBoxBaseProps {
  direction?: "row" | "column";
}

const getFlexBoxClassNames = (props: FlexBoxProps) => {
  return cn(
    "relative flex",
    {
      relative: true,
      flex: true,
      "flex-row": props.direction === "row",
      "flex-col": props.direction === "column",
      "justify-start": props.justify === "start",
      "justify-end": props.justify === "end",
      "justify-center": props.justify === "center",
      "justify-between": props.justify === "space-between",
      "items-start": props.align === "start",
      "items-end": props.align === "end",
      "items-center": props.align === "center",
      "self-start": props.alignSelf === "start",
      "self-end": props.alignSelf === "end",
      "self-center": props.alignSelf === "center",
      "self-stretch": props.alignSelf === "stretch",
      "flex-1": props.flex === true || props.scrollableParent,
      "overflow-hidden": props.scrollableParent,
      "overflow-y-auto": props.scrollable,
    },
    props.className,
  );
};

const getFlexStyle = (flex?: true | string) => {
  if (flex === true) {
    return "1";
  }
  return flex;
};

const FlexBox = forwardRef<HTMLDivElement, FlexBoxProps>(
  (
    {
      direction,
      justify,
      align,
      alignSelf,
      flex,
      children,
      scrollable = false,
      scrollableParent = false,
      className,
      ...rest
    }: FlexBoxProps,
    ref,
  ) => {
    const classNames = getFlexBoxClassNames({
      direction,
      justify,
      align,
      alignSelf,
      flex: flex === true ? true : undefined,
      scrollable,
      scrollableParent,
      className,
    });

    return (
      <div
        ref={ref}
        className={classNames}
        style={{ flex: getFlexStyle(flex) }}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

FlexBox.displayName = "FlexBox";
